import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Button } from "@legends/ui";
import {
  fetchCaveLegendMappings,
  linkLegendCave,
  linkLegendUser,
  type AuthzMe,
  type Cave,
  type CaveLegendMappings,
  type CaveMapping,
  type MappingLegend,
  type MappingUser,
} from "../api";
import { CaveEventsTab, CaveRollTab, CaveSecurityTab, CaveTicketsTab } from "../caveTabs";
import { AdminCaveRecordingsTab } from "../RecordingsTab";
import { AdminCaveEntitlementsTab } from "../EntitlementsTab";
import LobbyDesignerTab from "./LobbyDesignerTab";
import AdminModal from "./AdminModal";
import CaveFrontsTab from "./CaveFrontsTab";
import SearchableSelect from "./SearchableSelect";
import { caveSlug } from "./caveObject";

// The cave object page (admin redesign, Phase 2). A cave becomes a URL —
// /admin/caves/:slug — with the four cave-scoped screens (Events, Tickets, Fans,
// Recordings) as tabs, plus Access, Pre/post-roll and Settings, and an Overview
// that leads with the Cave → Legend → User readiness chain (the dissolved Mapping
// screen, scoped to this one cave). The cave comes from the URL, so the per-screen
// cave pickers are gone.

export const CAVE_TABS = [
  "overview",
  "events",
  "tickets",
  "fans",
  "designer",
  "recordings",
  "rolls",
  "caves",
  "access",
  "settings",
] as const;
export type CaveTab = (typeof CAVE_TABS)[number];

const TAB_LABEL: Record<CaveTab, string> = {
  overview: "Overview",
  events: "Events",
  tickets: "Tickets",
  fans: "Fans",
  designer: "Cave Designer",
  recordings: "Recordings",
  rolls: "Pre/post-roll",
  caves: "Caves",
  access: "Access",
  settings: "Settings",
};

// "unknown" is distinct from any setup state: it means the readiness data itself
// could not be loaded (still fetching, or the cave-mappings read failed), NOT that
// the cave is misconfigured. The header shows it neutrally so a transient data-load
// failure never reads as a definite "Can't go live" blocker (LSC-651).
type ReadinessState = "ready" | "no-legend" | "no-user" | "archived" | "unknown";

type Readiness = {
  legend?: { id: string; name: string };
  user?: { username: string };
  archived: boolean;
  state: Exclude<ReadinessState, "unknown">;
};

function readinessOf(m: CaveMapping | undefined): Readiness {
  if (!m) return { archived: false, state: "no-legend" };
  const legend = m.legendId ? { id: m.legendId, name: m.legendName ?? m.legendId } : undefined;
  const user = m.legendUsername ? { username: m.legendUsername } : undefined;
  const state: Readiness["state"] = m.archived
    ? "archived"
    : !legend
      ? "no-legend"
      : !user
        ? "no-user"
        : "ready";
  return { legend, user, archived: m.archived, state };
}

const STATUS: Record<ReadinessState, { label: string; color: string; tint: string }> = {
  ready: { label: "Live-ready", color: "var(--ok)", tint: "var(--ok-tint)" },
  "no-legend": { label: "Can't go live", color: "var(--warn)", tint: "var(--warn-tint)" },
  "no-user": { label: "Can't go live", color: "var(--warn)", tint: "var(--warn-tint)" },
  archived: { label: "Archived", color: "var(--text-3)", tint: "var(--neutral-tint)" },
  unknown: { label: "Readiness unavailable", color: "var(--text-3)", tint: "var(--neutral-tint)" },
};

function StatusBadge({ state }: { state: ReadinessState }) {
  const s = STATUS[state];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        height: "var(--h-badge)",
        padding: "0 10px",
        borderRadius: "var(--radius-pill)",
        background: s.tint,
        color: s.color,
        fontSize: "var(--fs-sm)",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      <span aria-hidden style={{ width: 7, height: 7, borderRadius: "50%", background: s.color }} />
      {s.label}
    </span>
  );
}

const cardStyle = {
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-lg)",
  padding: 20,
};

/** One link in the readiness chain — a labelled card, broken links in --live. */
function ChainCard({
  label,
  value,
  ok,
  missing,
}: {
  label: string;
  value: ReactNode;
  ok: boolean;
  missing: string;
}) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 150,
        background: "var(--surface-3)",
        border: `1px solid ${ok ? "var(--border)" : "var(--live-edge)"}`,
        borderRadius: "var(--radius)",
        padding: "12px 14px",
      }}
    >
      <div
        style={{
          fontSize: "var(--fs-micro)",
          letterSpacing: "var(--ls-caps)",
          textTransform: "uppercase",
          color: "var(--text-3)",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {ok ? (
        <div style={{ fontSize: "var(--fs-md)", color: "var(--text-1)", fontFamily: "var(--font-mono)" }}>{value}</div>
      ) : (
        <div style={{ fontSize: "var(--fs-md)", color: "var(--live)", fontWeight: 500 }}>{missing}</div>
      )}
    </div>
  );
}

function ReadinessCard({
  cave,
  r,
  legends,
  users,
  onReload,
}: {
  cave: Cave;
  r: Readiness;
  legends: MappingLegend[];
  users: MappingUser[];
  onReload: () => Promise<void>;
}) {
  // In-context linking (LSC-350): the operator attaches a legend / links a user
  // directly from the readiness card — no detour to the global Security screen,
  // which can't create the Legend↔User FK at all. Both write the durable link
  // (PUT /legends/:id/cave, PUT /legends/:id/user — ADR 0038) then re-read.
  const [picker, setPicker] = useState<null | "legend" | "user">(null);
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // A legend must be linked to THIS cave to headline it; only unlinked legends
  // (or one already on this cave) are offered.
  const legendOptions = legends.filter((l) => !l.caveId || l.caveId === cave.id);
  // A legend↔user link is 1:1, so offer accounts not already bound to a legend.
  const linkedUserIds = new Set(legends.map((l) => l.userId).filter(Boolean));
  const userOptions = users.filter((u) => !linkedUserIds.has(u.id));

  const open = (kind: "legend" | "user") => {
    setErr(null);
    setChoice("");
    setPicker(kind);
  };
  const submit = async () => {
    if (!choice) return;
    setBusy(true);
    setErr(null);
    try {
      if (picker === "legend") {
        await linkLegendCave(choice, cave.id);
      } else if (picker === "user" && r.legend) {
        await linkLegendUser(r.legend.id, choice);
      }
      await onReload();
      setPicker(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const explain =
    r.state === "ready"
      ? "This cave is ready to go on air."
      : r.state === "archived"
        ? "This cave is archived — its storefront is hidden."
        : "A cave with no legend is a dead storefront; a legend with no user can't go live.";
  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: "var(--fs-lg)", fontWeight: 600, letterSpacing: "var(--ls-display)", textTransform: "uppercase", color: "var(--text-1)" }}>
          Readiness
        </h2>
        <StatusBadge state={r.state} />
      </div>
      <div style={{ display: "flex", alignItems: "stretch", gap: 8, flexWrap: "wrap" }}>
        <ChainCard label="Cave" value={cave.name} ok missing="" />
        <div aria-hidden style={{ alignSelf: "center", color: r.legend ? "var(--text-3)" : "var(--live)" }}>→</div>
        <ChainCard label="Legend" value={r.legend?.name} ok={!!r.legend} missing="No legend attached" />
        <div aria-hidden style={{ alignSelf: "center", color: r.user ? "var(--text-3)" : "var(--live)" }}>→</div>
        <ChainCard label="User" value={r.user?.username} ok={!!r.user} missing="Legend has no user" />
      </div>
      <p style={{ margin: "12px 0 0", fontSize: "var(--fs-base)", color: "var(--text-2)", lineHeight: 1.5 }}>{explain}</p>
      {r.state !== "ready" && r.state !== "archived" && (
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          {!r.legend && (
            <Button size="sm" variant="gold" onClick={() => open("legend")}>Attach a legend</Button>
          )}
          {r.legend && !r.user && (
            <Button size="sm" variant="gold" onClick={() => open("user")}>Link a user</Button>
          )}
        </div>
      )}

      {picker && (
        <AdminModal
          title={picker === "legend" ? "Attach a legend to this cave" : `Link a user to ${r.legend?.name ?? "the legend"}`}
          confirmLabel={busy ? "Linking…" : picker === "legend" ? "Attach legend" : "Link user"}
          busy={busy}
          confirmDisabled={!choice}
          onConfirm={() => void submit()}
          onCancel={() => { if (!busy) setPicker(null); }}
        >
          <p style={{ margin: "0 0 12px", fontSize: "var(--fs-sm)", color: "var(--text-2)" }}>
            {picker === "legend"
              ? "Pick the legend this cave headlines. A legend headlines one Cave Backend, so a legend already headlining another Cave isn't listed here. To run one legend on multiple storefronts, don't make a second cave — add a Cave (front) from the Caves tab; it shares this backend's legend automatically."
              : "Pick the account this legend signs in as to go live. This creates the durable Legend↔User link."}
          </p>
          <label style={{ display: "grid", gap: 4 }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--fs-2xs)", letterSpacing: "var(--ls-label)", textTransform: "uppercase", color: "var(--text-3)" }}>
              {picker === "legend" ? "Legend" : "User"}
            </span>
            {/* Searchable (LSC-371): these lists run to the full roster/directory,
                so type-to-filter instead of scrolling a native dropdown. */}
            <SearchableSelect
              value={choice}
              onChange={setChoice}
              ariaLabel={picker === "legend" ? "Legend" : "User"}
              placeholder={picker === "legend" ? "Search legends…" : "Search users…"}
              options={
                picker === "legend"
                  ? legendOptions.map((l) => ({ value: l.id, label: `${l.name} (${l.sport})` }))
                  : userOptions.map((u) => ({ value: u.username, label: u.username }))
              }
            />
          </label>
          {((picker === "legend" && legendOptions.length === 0) || (picker === "user" && userOptions.length === 0)) && (
            <p style={{ margin: "8px 0 0", fontSize: "var(--fs-sm)", color: "var(--text-3)" }}>
              {picker === "legend"
                ? "No legends available — every legend is either headlining another Cave (a legend headlines one backend) or none exist yet. For a second storefront on the same legend, use the Caves tab; otherwise create a legend in the Legends admin first."
                : "No unlinked users — create an account in Security first."}
            </p>
          )}
          {err && <p role="alert" style={{ margin: "8px 0 0", fontSize: "var(--fs-sm)", color: "var(--live, #e8735a)" }}>{err}</p>}
        </AdminModal>
      )}
    </div>
  );
}

export interface CaveDetailScreenProps {
  cave: Cave;
  tab: CaveTab;
  authz: AuthzMe;
  onOpenTab: (slug: string, tab: CaveTab) => void;
  onOpenCaves: () => void;
}

export default function CaveDetailScreen({ cave, tab, authz, onOpenTab, onOpenCaves }: CaveDetailScreenProps) {
  const slug = caveSlug(cave);
  const [mappings, setMappings] = useState<CaveLegendMappings | undefined>();
  const [mapError, setMapError] = useState(false);

  // Remember the last cave opened, so the legacy /admin/events redirect can land
  // the operator on the cave they were last working in.
  useEffect(() => {
    try {
      localStorage.setItem("admin.lastCaveSlug", slug);
    } catch {
      /* private mode / blocked storage — the redirect just falls back to /admin/caves */
    }
  }, [slug]);

  // The readiness chain reuses the cave-mappings join (the dissolved Mapping
  // screen's data). We keep the WHOLE response — the caves join + the legend/user
  // rosters — so the readiness card can link a legend or a user in-context
  // (LSC-350) and re-read to reflect it. A per-cave endpoint would be cheaper.
  const reloadMappings = useCallback(() => {
    return fetchCaveLegendMappings()
      .then(setMappings)
      .catch(() => setMapError(true));
  }, []);
  useEffect(() => {
    void reloadMappings();
  }, [reloadMappings]);

  const mapping = mappings?.caves.find((c) => c.id === cave.id);
  const r = useMemo(() => readinessOf(mapping), [mapping]);
  // Until the cave-mappings join resolves (or if it failed), readiness is UNKNOWN —
  // not a blocker. Only a successfully-loaded mapping yields a real setup verdict,
  // so a 500/slow read never mislabels a healthy cave "Can't go live" (LSC-651).
  const headerState: ReadinessState = mapError || mappings === undefined ? "unknown" : r.state;
  const host = cave.host || "";
  const hostUrl = host ? `https://${host}` : undefined;

  return (
    <div style={{ maxWidth: "var(--max-content)" }}>
      {/* breadcrumb */}
      <nav aria-label="Breadcrumb" style={{ fontSize: "var(--fs-sm)", color: "var(--text-3)", marginBottom: 8 }}>
        <button
          type="button"
          onClick={onOpenCaves}
          style={{ background: "none", border: "none", padding: 0, color: "var(--text-2)", cursor: "pointer", font: "inherit" }}
        >
          Caves
        </button>
        <span aria-hidden style={{ margin: "0 8px" }}>/</span>
        <span style={{ color: "var(--text-2)" }}>{cave.name}</span>
      </nav>

      {/* header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 16 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <h1 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: "var(--fs-display)", fontWeight: 600, letterSpacing: "var(--ls-display)", textTransform: "uppercase", color: "var(--text-1)" }}>
              {cave.name}
            </h1>
            <StatusBadge state={headerState} />
          </div>
          <div style={{ display: "flex", gap: 16, marginTop: 6, fontSize: "var(--fs-sm)", color: "var(--text-3)", fontFamily: "var(--font-mono)" }}>
            {hostUrl ? (
              <a href={hostUrl} target="_blank" rel="noopener noreferrer" style={{ color: "var(--text-2)" }}>
                {host} ↗
              </a>
            ) : (
              <span>{cave.id}</span>
            )}
            <span>tenant: {cave.tenant}</span>
          </div>
        </div>
        {/* primary action — stays visible when blocked, disabled with the reason on the Readiness card.
            When readiness is unavailable (data didn't load) the button is disabled but labelled
            "unavailable", not "blocked", so it doesn't read as a setup problem (LSC-651). */}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {headerState === "ready" ? (
            <Button variant="gold" onClick={() => onOpenTab(slug, "events")}>Go live</Button>
          ) : (
            <Button
              variant="gold"
              disabled
              aria-label={
                headerState === "unknown"
                  ? "Go live — readiness unavailable"
                  : `Go live — blocked: ${STATUS[headerState].label}`
              }
            >
              Go live
            </Button>
          )}
        </div>
      </div>

      {/* tabs */}
      <div role="tablist" aria-label="Cave sections" style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--border)", marginBottom: 20, overflowX: "auto" }}>
        {CAVE_TABS.map((t) => {
          const active = t === tab;
          return (
            <button
              key={t}
              role="tab"
              aria-selected={active}
              type="button"
              onClick={() => onOpenTab(slug, t)}
              style={{
                background: "none",
                border: "none",
                borderBottom: `2px solid ${active ? "var(--gold-500)" : "transparent"}`,
                padding: "0 12px",
                height: "var(--h-tab)",
                color: active ? "var(--text-1)" : "var(--text-3)",
                fontSize: "var(--fs-md)",
                fontWeight: active ? 600 : 400,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {TAB_LABEL[t]}
            </button>
          );
        })}
      </div>

      {/* content */}
      {tab === "overview" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {mapError ? (
            // Readiness data unavailable — explicitly NOT a setup blocker, with a retry.
            // The cave's assignments/events are unaffected; only this read failed (LSC-651).
            <div role="alert" style={cardStyle}>
              <p style={{ margin: "0 0 4px", color: "var(--text-1)", fontWeight: 600 }}>Readiness unavailable</p>
              <p style={{ margin: "0 0 12px", color: "var(--text-3)", fontSize: "var(--fs-sm)" }}>
                We couldn't load this cave's readiness right now. This doesn't mean anything is misconfigured —
                the data just didn't load.
              </p>
              <Button
                variant="default"
                onClick={() => {
                  setMapError(false);
                  void reloadMappings();
                }}
              >
                Retry
              </Button>
            </div>
          ) : mappings === undefined ? (
            <p style={{ margin: 0, color: "var(--text-3)" }}>Loading readiness…</p>
          ) : (
            <ReadinessCard
              cave={cave}
              r={r}
              legends={mappings.legends}
              users={mappings.users}
              onReload={reloadMappings}
            />
          )}
        </div>
      )}
      {tab === "events" && (
        <CaveEventsTab
          caveId={cave.id}
          canWrite={authz.catalogWrite}
          // Assign event production from the account directory (LSC-403) — the admin cave
          // page already has the roster from the cave-mappings join (readiness card).
          userOptions={mappings?.users.map((u) => ({ value: u.username, label: u.username }))}
        />
      )}
      {tab === "tickets" && <CaveTicketsTab caveId={cave.id} canWrite={authz.catalogWrite} />}
      {tab === "fans" && <AdminCaveEntitlementsTab caveId={cave.id} rootAdmin={authz.rootAdmin} />}
      {tab === "designer" && (
        <LobbyDesignerTab caveId={cave.id} caveName={cave.name} canPublish={authz.superAdmin} />
      )}
      {tab === "recordings" && <AdminCaveRecordingsTab caveId={cave.id} />}
      {tab === "rolls" && <CaveRollTab caveId={cave.id} />}
      {tab === "caves" && <CaveFrontsTab caveId={cave.id} caveName={cave.name} canWrite={authz.catalogWrite} />}
      {tab === "access" && <CaveSecurityTab caveId={cave.id} tenant={cave.tenant} canProvision={authz.canManageAuthz} />}
      {tab === "settings" && <CaveSettings cave={cave} />}
    </div>
  );
}

/** Minimal cave settings surface — the host + tenant are read-only facts; archive/
 *  delete live on the Caves list (super-admin ConfirmDialog). Fuller cave config
 *  (recording default, delivery) is a later refinement. */
function CaveSettings({ cave }: { cave: Cave }) {
  return (
    <div style={{ ...cardStyle, maxWidth: 640 }}>
      <h2 style={{ margin: "0 0 12px", fontFamily: "var(--font-display)", fontSize: "var(--fs-lg)", fontWeight: 600, letterSpacing: "var(--ls-display)", textTransform: "uppercase", color: "var(--text-1)" }}>
        Settings
      </h2>
      <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "auto 1fr", gap: "8px 16px", fontSize: "var(--fs-md)" }}>
        <dt style={{ color: "var(--text-3)" }}>Name</dt>
        <dd style={{ margin: 0, color: "var(--text-1)" }}>{cave.name}</dd>
        <dt style={{ color: "var(--text-3)" }}>Host</dt>
        <dd style={{ margin: 0, color: "var(--text-1)", fontFamily: "var(--font-mono)" }}>{cave.host || "—"}</dd>
        <dt style={{ color: "var(--text-3)" }}>Tenant</dt>
        <dd style={{ margin: 0, color: "var(--text-1)", fontFamily: "var(--font-mono)" }}>{cave.tenant}</dd>
        <dt style={{ color: "var(--text-3)" }}>Recording default</dt>
        <dd style={{ margin: 0, color: "var(--text-1)" }}>
          {cave.recordDefault == null ? "Inherit platform default" : cave.recordDefault ? "On" : "Off"}
        </dd>
      </dl>
      <p style={{ margin: "16px 0 0", fontSize: "var(--fs-base)", color: "var(--text-3)", lineHeight: 1.5 }}>
        Archive or delete this cave from the Caves list. Recording default and delivery
        are configured per event on the Events tab.
      </p>
    </div>
  );
}
